import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { Button, Dropdown, Typography } from '@cobliteam/ui/components';
import { InputMask } from '@cobliteam/ui/form';

import { endOfDay, format, startOfDay } from '@cobliteam/utils/date';

import Icon from '@cobliteam/ui/icons';

import type { ButtonProps } from '@cobliteam/ui/components/Button';

interface TimeWindow {
  startTime: string;
  endTime: string;
}

interface TimeValues {
  hours: number;
  minutes: number;
}

interface SelectTimeWindowProps {
  size?: ButtonProps['size'];
  internalLabel?: string;
  selectedTimeWindow?: [number, number];
  minStartTime?: number;
  maxEndTime?: number;
  onTimeChange: (start: number, end: number) => void;
}

const SelectTimeWindow = ({
  size = 'md',
  internalLabel = 'Mostrar trajeto de:',
  selectedTimeWindow,
  minStartTime = startOfDay(Date.now()).valueOf(),
  maxEndTime = endOfDay(Date.now()).valueOf(),
  onTimeChange,
}: SelectTimeWindowProps) => {
  const searchParams = new URLSearchParams(location.search);

  // Get the initial applied time window from the search params
  const initialAppliedTimeWindow = useMemo(() => {
    const start = searchParams.get('start');
    const end = searchParams.get('end');

    if (start && end) {
      const startDate = new Date(Number.parseInt(start));
      const endDate = new Date(Number.parseInt(end));

      return {
        startTime: format(startDate, 'HH:mm'),
        endTime: format(endDate, 'HH:mm'),
      };
    }

    return null;
  }, [searchParams]);

  // Calculate initial boundary times from the timestamps
  const boundaries = useMemo(
    () => ({
      min: format(selectedTimeWindow?.[0] ?? new Date(minStartTime), 'HH:mm'),
      max: format(selectedTimeWindow?.[1] ?? new Date(maxEndTime), 'HH:mm'),
    }),
    [selectedTimeWindow, minStartTime, maxEndTime]
  );

  const [timeWindow, setTimeWindow] = useState<TimeWindow>({
    startTime: boundaries.min,
    endTime: boundaries.max,
  });
  const [appliedTimeWindow, setAppliedTimeWindow] = useState<TimeWindow | null>(initialAppliedTimeWindow);
  const [forceClose, setForceClose] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(!!initialAppliedTimeWindow);

  // Normalize the time values into a string
  const normalizeTime = useCallback((timeValues: TimeValues): string => {
    const validHours = Math.min(Math.max(timeValues.hours, 0), 23);
    const validMinutes = Math.min(Math.max(timeValues.minutes, 0), 59);
    return `${validHours.toString().padStart(2, '0')}:${validMinutes.toString().padStart(2, '0')}`;
  }, []);

  // Parse the time string into time values
  const parseTimeString = useCallback((timeStr: string): TimeValues => {
    const cleanValue = timeStr.replace(/[^0-9]/g, '');
    return {
      hours: Number.parseInt(cleanValue.slice(0, 2) || '0', 10),
      minutes: Number.parseInt(cleanValue.slice(2, 4) || '0', 10),
    };
  }, []);

  // Validate the time input when the user leaves the input field
  const getValidatedTime = useCallback((currentTime: TimeValues, minTime: TimeValues, maxTime: TimeValues): TimeValues => {
    if (currentTime.hours < minTime.hours || (currentTime.hours === minTime.hours && currentTime.minutes < minTime.minutes)) {
      return minTime;
    }

    if (currentTime.hours > maxTime.hours || (currentTime.hours === maxTime.hours && currentTime.minutes > maxTime.minutes)) {
      return maxTime;
    }

    return currentTime;
  }, []);

  // Validate the time input when the user leaves the input field
  const handleTimeBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>, field: keyof TimeWindow, minValue: string, maxValue: string) => {
      const currentTime = parseTimeString(event.target.value);
      const minTime = parseTimeString(minValue);
      const maxTime = parseTimeString(maxValue);

      const validatedTime = getValidatedTime(currentTime, minTime, maxTime);
      const normalizedTimeString = normalizeTime(validatedTime);

      setTimeWindow((prev) => ({ ...prev, [field]: normalizedTimeString }));
    },
    [normalizeTime, parseTimeString, getValidatedTime]
  );

  // Create a timestamp from the time values
  const createTimeFromValues = useCallback((date: number, timeValues: TimeValues, isEndTime: boolean): number => {
    const baseDate = new Date(date);
    return new Date(baseDate.setHours(timeValues.hours, timeValues.minutes, isEndTime ? 59 : 0, isEndTime ? 999 : 0)).getTime();
  }, []);

  // Apply the filter when the user clicks on the apply button
  const handleFilterTime = useCallback(() => {
    const startTimeValues = parseTimeString(timeWindow.startTime);
    const endTimeValues = parseTimeString(timeWindow.endTime);

    const start = createTimeFromValues(minStartTime, startTimeValues, false);
    const end = createTimeFromValues(maxEndTime, endTimeValues, true);

    setAppliedTimeWindow(timeWindow);
    setForceClose(true);
    setIsFilterApplied(true);
    onTimeChange(start, end);
  }, [timeWindow, minStartTime, maxEndTime, onTimeChange, parseTimeString, createTimeFromValues]);

  // Get the initial time window based on the boundaries
  const getInitialTimeWindow = useCallback(
    (): TimeWindow => ({
      startTime: boundaries.min,
      endTime: boundaries.max,
    }),
    [boundaries]
  );

  // When clearing the filter, use the initial time window or the current boundaries
  const handleClearFilter = useCallback(() => {
    const initialTimeWindow = getInitialTimeWindow();
    setTimeWindow(initialTimeWindow);
    setAppliedTimeWindow(null);
    setForceClose(true);
    setIsFilterApplied(false);
    onTimeChange(minStartTime, maxEndTime);
  }, [minStartTime, maxEndTime, onTimeChange, getInitialTimeWindow]);

  // When opening the dropdown, use either the applied filter or the current boundaries
  const handleDropdownOpen = useCallback(() => {
    const newTimeWindow = appliedTimeWindow ?? getInitialTimeWindow();
    setTimeWindow(newTimeWindow);
    setForceClose(false);
  }, [appliedTimeWindow, getInitialTimeWindow]);

  // When canceling the filter, use the initial applied time window or the current boundaries
  const handleCancel = useCallback(() => {
    const newTimeWindow = appliedTimeWindow ?? getInitialTimeWindow();
    setTimeWindow(newTimeWindow);
    setForceClose(true);
  }, [appliedTimeWindow, getInitialTimeWindow]);

  return (
    <Dropdown
      forceClose={forceClose}
      overlay={
        <div className='flex w-96 flex-col gap-2.5 rounded-md border border-gray-light bg-white py-2.5'>
          <Dropdown.Header className='px-5 py-1'>
            <Button variant='text' size={size} disabled={!isFilterApplied} onClick={handleClearFilter}>
              Limpar filtro
            </Button>
          </Dropdown.Header>

          <Dropdown.Body className='my-2.5 flex flex-row items-center gap-2 px-5'>
            <Typography variant='caption-default'>{internalLabel}</Typography>

            <InputMask
              value={timeWindow.startTime}
              onChange={(e) =>
                setTimeWindow((prev) => ({
                  ...prev,
                  startTime: e.target.value,
                }))
              }
              onBlur={(e) => handleTimeBlur(e, 'startTime', boundaries.min, timeWindow.endTime)}
              mask='__:__'
              replacement={{ _: /\d/ }}
              className='w-20'
            />

            <Typography variant='caption-default'>até</Typography>

            <InputMask
              value={timeWindow.endTime}
              onChange={(e) => setTimeWindow((prev) => ({ ...prev, endTime: e.target.value }))}
              onBlur={(e) => handleTimeBlur(e, 'endTime', timeWindow.startTime, boundaries.max)}
              mask='__:__'
              replacement={{ _: /\d/ }}
              className='w-20'
            />
          </Dropdown.Body>

          <Dropdown.Footer>
            <Dropdown.Actions className='px-5 py-1'>
              <Button variant='text' full size='sm' onClick={handleCancel}>
                Cancelar
              </Button>
              <Button size='sm' full onClick={handleFilterTime}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </div>
      }
    >
      <Button variant='text' size='sm' startIcon={<Icon name='clock' />} onClick={handleDropdownOpen}>
        Horário {appliedTimeWindow && `(${appliedTimeWindow.startTime} - ${appliedTimeWindow.endTime})`}
      </Button>
    </Dropdown>
  );
};

export default SelectTimeWindow;
